import {
  AssessmentType,
  CompletedProgramAssessmentsDataQuery,
} from '../../../generated/graphql';
import { AssessmentCompletionDataTypename } from '../../lib/assessments';
import { getTimeStamp } from '../../lib/time';

export type CompletedProgramAssessment =
  CompletedProgramAssessmentsDataQuery['completedProgramAssessmentsByAssessment'][number];

export type ScoreProps = {
  score?: number;
  categorization?: string;
  color?: string;
  hasPercentage?: boolean;
  shortLabel?: string;
};

const GRAPH_POINT_LIMIT = 7;

const isTwoSubscoreCompletion = (typename?: string | null) =>
  typename === AssessmentCompletionDataTypename.HADSAssessmentCompletionData ||
  typename === AssessmentCompletionDataTypename.ASRS19AssessmentCompletionData;

const CBI_SHORT_LABEL: Record<string, string> = {
  PersonalBurnout: 'Personal',
  WorkRelatedBurnout: 'Work',
  ClientRelatedBurnout: 'Client',
};

export const getScoreProps = (
  completedAssessment: CompletedProgramAssessment | undefined,
  assessmentType: AssessmentType | undefined,
) => {
  let primary: ScoreProps = {};
  let secondary: ScoreProps = {};
  let tertiary: ScoreProps = {};

  const typename = completedAssessment?.completionData?.__typename;

  if (
    typename ===
    AssessmentCompletionDataTypename.MultipleChoiceSumScoredAssessmentCompletionData
  ) {
    primary = {
      ...completedAssessment?.completionData,
      hasPercentage: assessmentType === AssessmentType.Meq30,
    };
  }

  if (isTwoSubscoreCompletion(typename)) {
    const { subscores } = completedAssessment?.completionData as {
      subscores: ScoreProps[];
    };
    primary = { ...subscores[0] };
    secondary = { ...subscores[1] };
  }

  if (
    typename === AssessmentCompletionDataTypename.CBIAssessmentCompletionData
  ) {
    const { subscores } = completedAssessment?.completionData as {
      subscores: Array<ScoreProps & { subscale?: string | null }>;
    };
    primary = {
      ...subscores[0],
      shortLabel: CBI_SHORT_LABEL[subscores[0]?.subscale ?? ''] ?? 'Personal',
    };
    secondary = {
      ...subscores[1],
      shortLabel: CBI_SHORT_LABEL[subscores[1]?.subscale ?? ''] ?? 'Work',
    };
    tertiary = {
      ...subscores[2],
      shortLabel: CBI_SHORT_LABEL[subscores[2]?.subscale ?? ''] ?? 'Client',
    };
  }

  return {
    primary,
    secondary,
    tertiary,
  };
};

export const getCompletedAssessmentGraphData = (
  completedProgramAssessments: CompletedProgramAssessment[] | undefined,
) => {
  let hasSecondaryScore = false;
  let hasTertiaryScore = false;

  const graphData = completedProgramAssessments
    ?.map((completedProgramAssessment, completedProgramAssessmentIndex) => {
      const typename = completedProgramAssessment?.completionData?.__typename;
      const timestamp = getTimeStamp(
        completedProgramAssessment.completedAt,
        false,
        false,
        false,
      );

      if (
        typename ===
        AssessmentCompletionDataTypename.MultipleChoiceSumScoredAssessmentCompletionData
      ) {
        return {
          completedProgramAssessmentIndex,
          primaryScore: completedProgramAssessment?.completionData.score,
          primaryColor: completedProgramAssessment?.completionData.color,
          timestamp,
        };
      }
      if (isTwoSubscoreCompletion(typename)) {
        if (!hasSecondaryScore) hasSecondaryScore = true;
        const { subscores } = completedProgramAssessment?.completionData as {
          subscores: Array<{ score?: number; color?: string }>;
        };
        const primary = { ...subscores[0] };
        const secondary = { ...subscores[1] };
        return {
          completedProgramAssessmentIndex,
          primaryScore: primary.score,
          primaryColor: primary.color,
          secondaryScore: secondary.score,
          secondaryColor: secondary.color,
          timestamp,
        };
      }
      if (
        typename ===
        AssessmentCompletionDataTypename.CBIAssessmentCompletionData
      ) {
        if (!hasSecondaryScore) hasSecondaryScore = true;
        if (!hasTertiaryScore) hasTertiaryScore = true;
        const { subscores } = completedProgramAssessment?.completionData as {
          subscores: Array<{ score?: number; color?: string }>;
        };
        return {
          completedProgramAssessmentIndex,
          primaryScore: subscores[0]?.score,
          primaryColor: subscores[0]?.color,
          secondaryScore: subscores[1]?.score,
          secondaryColor: subscores[1]?.color,
          tertiaryScore: subscores[2]?.score,
          tertiaryColor: subscores[2]?.color,
          timestamp,
        };
      }
      return {};
    })
    .slice(0, GRAPH_POINT_LIMIT)
    .reverse();

  let graphTitle = '';
  if (graphData?.length) graphTitle += `${graphData[0].timestamp}`;
  if (graphData?.length && graphData.length > 1)
    graphTitle += ` - ${graphData[graphData.length - 1].timestamp}`;

  const totalCompletions = completedProgramAssessments?.length ?? 0;
  if (totalCompletions > GRAPH_POINT_LIMIT) {
    graphTitle = graphTitle
      ? `${graphTitle} · Last ${GRAPH_POINT_LIMIT} completions`
      : `Last ${GRAPH_POINT_LIMIT} completions`;
  }

  return {
    graphData,
    hasSecondaryScore,
    hasTertiaryScore,
    graphTitle,
    totalCompletions,
  };
};
